import { BulbIcon, ChartIncreaseIcon, CheckmarkCircle01Icon, SparklesIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useAnalyzeSurveyResponsesMutation } from "@/api/surveysApi";
import useSnackbar from "@/hooks/useSnackbar";
import {
  AnalyzeSurveyQuestionInput,
  QuestionStats,
  Survey,
  SurveyAIAnalysis,
  SurveyRecentComment,
} from "@/types/surveys";

interface SurveyAIAnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  survey: Survey;
  totalResponses: number;
  stats: Record<string, QuestionStats>;
  recentComments: SurveyRecentComment[];
}

const SurveyAIAnalysisDialog = ({
  open,
  onClose,
  survey,
  totalResponses,
  stats,
  recentComments,
}: SurveyAIAnalysisDialogProps) => {
  const [openSnackbar] = useSnackbar();
  const [aiAnalysis, setAiAnalysis] = useState<SurveyAIAnalysis | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [analyzeResponses, analyzeState] = useAnalyzeSurveyResponsesMutation();
  const hasAutoTriggeredRef = useRef(false);

  // Mantener referencias actualizadas de las props sin causar recreación de funciones
  const latestPropsRef = useRef({
    survey,
    totalResponses,
    stats,
    recentComments,
  });

  useEffect(() => {
    latestPropsRef.current = {
      survey,
      totalResponses,
      stats,
      recentComments,
    };
  });

  // Limpiar estado si cambia de encuesta
  useEffect(() => {
    setAiAnalysis(null);
    setErrorMessage(null);
    hasAutoTriggeredRef.current = false;
  }, [survey.id]);

  const handleAnalyzeResponses = useCallback(async () => {
    const {
      survey: currentSurvey,
      totalResponses: currentTotal,
      stats: currentStats,
      recentComments: currentComments,
    } = latestPropsRef.current;

    if (currentTotal === 0) {
      openSnackbar("Aún no hay respuestas registradas para analizar", "warning");
      return;
    }

    setErrorMessage(null);

    const questionsPayload: AnalyzeSurveyQuestionInput[] = (currentSurvey.questions ?? []).map(
      (q, idx) => {
        const qKey = q.id || String(idx);
        const qStat = currentStats[qKey];
        const qTotal = qStat?.total ?? 0;
        const qType = q.type || "single_choice";

        const averageRating =
          qType === "rating" && qTotal > 0 && typeof qStat?.sum === "number"
            ? Number((qStat.sum / qTotal).toFixed(2))
            : null;

        const optionsBreakdown =
          qType === "single_choice" || qType === "multiple_choice"
            ? (q.options ?? []).map((opt, optIdx) => {
              const optKey = opt.id || String(optIdx);
              const count = qStat?.options?.[optKey] ?? 0;
              const percentage = qTotal > 0 ? Math.round((count / qTotal) * 100) : 0;
              return {
                title: opt.title,
                count,
                percentage,
              };
            })
            : undefined;

        const textAnswers =
          qType === "text"
            ? currentComments
              .filter((c) => c.questionId === qKey)
              .map((c) => c.text)
              .filter(Boolean)
            : undefined;

        return {
          title: q.title,
          type: qType,
          totalResponses: qTotal,
          averageRating,
          ratingDistribution: qType === "rating" ? qStat?.distribution : undefined,
          optionsBreakdown,
          textAnswers,
        };
      }
    );

    try {
      const result = await analyzeResponses({
        title: currentSurvey.title,
        description: currentSurvey.description,
        totalResponses: currentTotal,
        questions: questionsPayload,
      }).unwrap();

      setAiAnalysis(result);
      setErrorMessage(null);
      openSnackbar("Análisis generado con IA exitosamente", "success");
    } catch (err: any) {
      const rawMsg =
        err?.data?.statusText ||
        err?.statusText ||
        err?.message ||
        "";
      const friendlyMsg =
        !rawMsg || rawMsg.toLowerCase() === "internal"
          ? "No se pudo completar el análisis con IA en este momento. Por favor intenta de nuevo."
          : rawMsg;
      setErrorMessage(friendlyMsg);
      openSnackbar(friendlyMsg, "error");
    }
  }, [analyzeResponses, openSnackbar]);

  // Ejecutar el análisis automáticamente una sola vez al abrir el diálogo
  useEffect(() => {
    if (open) {
      if (!hasAutoTriggeredRef.current && !aiAnalysis && totalResponses > 0) {
        hasAutoTriggeredRef.current = true;
        handleAnalyzeResponses();
      }
    } else {
      // Permitir que al volver a abrir después de cerrar pueda dispararse si aún no hay resultado
      hasAutoTriggeredRef.current = false;
    }
  }, [open, aiAnalysis, totalResponses, handleAnalyzeResponses]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              padding: 1,
              backgroundColor: "rgba(1, 95, 238, 0.1)",
              color: "#015FEE",
            }}
          >
            <HugeiconsIcon icon={SparklesIcon} size={24} />
          </Avatar>
          <Stack>
            <Typography variant="h3">
              Análisis Inteligente de Respuestas
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Resumen generado con IA basado en {totalResponses} respuestas de "{survey.title}"
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {analyzeState.isLoading && !aiAnalysis ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" py={6}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Analizando respuestas de tus clientes con IA...
            </Typography>
          </Stack>
        ) : aiAnalysis ? (
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f8fafc",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack spacing={0.75}>
                <Typography variant="subtitle2" color="primary.main">
                  ¿Qué están pensando tus clientes?
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {aiAnalysis.summary}
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              {aiAnalysis.strengths?.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: "100%",
                      boxSizing: "border-box",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} color="#16a34a" />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#15803d" }}
                        >
                          Lo que más valoran
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5} component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {aiAnalysis.strengths.map((item, i) => (
                          <Typography
                            key={i}
                            component="li"
                            variant="body2"
                            sx={{ color: "#166534" }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {aiAnalysis.opportunities?.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: "100%",
                      boxSizing: "border-box",
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HugeiconsIcon icon={ChartIncreaseIcon} size={18} color="#d97706" />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#b45309" }}
                        >
                          Oportunidades de mejora
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5} component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {aiAnalysis.opportunities.map((item, i) => (
                          <Typography
                            key={i}
                            component="li"
                            variant="body2"
                            sx={{ color: "#92400e" }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {aiAnalysis.recommendation && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <HugeiconsIcon icon={BulbIcon}
                    size={20}
                    color="#015FEE"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <Stack spacing={0.25}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#1e40af" }}
                    >
                      Recomendación práctica
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#1e3a8a" }}>
                      {aiAnalysis.recommendation}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        ) : errorMessage ? (
          <Stack spacing={2} alignItems="center" py={4}>
            <Typography variant="body2" color="error" textAlign="center">
              {errorMessage}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAnalyzeResponses}
              disabled={analyzeState.isLoading || totalResponses === 0}
              startIcon={<HugeiconsIcon icon={SparklesIcon} size={16} />}
            >
              Reintentar análisis
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No se pudo obtener el análisis en este momento. Intenta actualizar el análisis.
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 3 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cerrar
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={handleAnalyzeResponses}
          disabled={analyzeState.isLoading || totalResponses === 0}
          startIcon={
            analyzeState.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <HugeiconsIcon icon={SparklesIcon} size={18} />
            )
          }
        >
          {analyzeState.isLoading ? "Analizando..." : "Actualizar análisis"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyAIAnalysisDialog;
